/**
 * Main Calendar Component
 * Full-featured calendar with day/week/month views
 * Supports appointments, meetings, and member scheduling
 */

import React, { useState, useMemo, useCallback } from 'react';
import {
  format,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameDay,
  addMonths,
  subMonths,
  addWeeks,
  subWeeks,
  addDays,
  subDays,
  getHours,
  setHours,
  setMinutes,
} from 'date-fns';
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Plus,
  RefreshCw,
  Video,
  MapPin,
  Phone,
  Clock,
  Users,
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { calendarAPI, CalendarEvent, EventFilters, EVENT_TYPE_LABELS, LOCATION_TYPE_LABELS } from '@/services/api/calendar';
import EventModal from './EventModal';
import { cn } from '@/lib/utils';

// Activity-tracking event types route to the full-screen EventDetailPage
// inside the Activity Tracking tab (not the appointment-shaped EventModal).
// Calendar.tsx is shared across many surfaces, so we hand off via
// sessionStorage + the onNavigate prop on Calendar's parent (CalendarPage)
// when available; otherwise we fall back to EventModal. Keep this list in
// sync with the EventType union in services/api/calendar.ts.
const ACTIVITY_TRACKING_EVENT_TYPES = new Set(['network_hub_meeting', 'training_workshop']);

/** Key the EventTrackingPage reads on mount to deep-link to a specific
 *  event detail view (cross-tab nav since the app uses tab-state, not URLs). */
export const ACTIVITY_EVENT_DEEPLINK_KEY = 'activity_tracking_open_event_id';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { toZonedWallDate, zoneAbbreviation, zonedNow } from '@/utils/zonedTime';
import { ItemZoneNote, useItemZoneNote } from '@/components/ui/ItemZoneNote';

type CalendarView = 'day' | 'week' | 'month';

interface CalendarProps {
  userId?: number;
  memberId?: number;
  memberName?: string;
  onEventClick?: (event: CalendarEvent) => void;
  onSlotClick?: (date: Date) => void;
  className?: string;
  defaultView?: CalendarView;
  showCreateButton?: boolean;
  /** Tab-switcher from the app shell. When provided, clicking an
   *  activity-tracking event routes to the event-tracking tab instead of
   *  opening the appointment-shaped EventModal. */
  onNavigate?: (tab: string) => void;
}

export default function Calendar({
  userId,
  memberId,
  memberName,
  onEventClick,
  onSlotClick,
  className,
  defaultView = 'week',
  showCreateButton = true,
  onNavigate,
}: CalendarProps) {
  const { t } = useAppTranslation();
  const { fmt, timezone: tz } = useFormattedDate();
  const queryClient = useQueryClient();

  // TRUS-695: the grid's days and hours are wall-clock time in the user's
  // selected zone (Settings > Preferences), not the browser's.
  const [currentDate, setCurrentDate] = useState(() => zonedNow(tz));
  const isTodayInZone = (day: Date) => isSameDay(day, zonedNow(tz));
  const [view, setView] = useState<CalendarView>(defaultView);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<Date | null>(null);

  // Calculate date range based on view
  const dateRange = useMemo(() => {
    if (view === 'month') {
      const start = startOfWeek(startOfMonth(currentDate));
      const end = endOfWeek(endOfMonth(currentDate));
      return { start, end };
    }
    if (view === 'week') {
      return {
        start: startOfWeek(currentDate),
        end: endOfWeek(currentDate),
      };
    }
    return {
      start: currentDate,
      end: currentDate,
    };
  }, [currentDate, view]);

  // Fetch events
  const { data: eventsData, isLoading, refetch } = useQuery({
    queryKey: ['calendar-events', dateRange.start.toISOString(), dateRange.end.toISOString(), userId, memberId, tz],
    queryFn: () => {
      const filters: EventFilters = {
        start_date: format(dateRange.start, 'yyyy-MM-dd'),
        end_date: format(dateRange.end, 'yyyy-MM-dd'),
        page_size: 200,
        // The range is these days in the user's zone (the server's default too).
        tz,
      };
      if (userId) filters.user_id = userId;
      if (memberId) filters.member_id = memberId;

      return memberId
        ? calendarAPI.getMemberEvents(memberId, filters)
        : calendarAPI.getMyEvents(filters);
    },
  });

  const events = eventsData?.events || [];

  // Generate days array based on view
  const days = useMemo(() => {
    if (view === 'month') {
      return eachDayOfInterval({ start: dateRange.start, end: dateRange.end });
    }
    if (view === 'week') {
      return eachDayOfInterval({ start: dateRange.start, end: dateRange.end });
    }
    return [currentDate];
  }, [currentDate, view, dateRange]);

  // Navigation handlers
  const handleNavigate = (direction: 'prev' | 'next' | 'today') => {
    if (direction === 'today') {
      setCurrentDate(zonedNow(tz));
      return;
    }

    const delta = direction === 'prev' ? -1 : 1;
    if (view === 'month') {
      setCurrentDate(delta > 0 ? addMonths(currentDate, 1) : subMonths(currentDate, 1));
    } else if (view === 'week') {
      setCurrentDate(delta > 0 ? addWeeks(currentDate, 1) : subWeeks(currentDate, 1));
    } else {
      setCurrentDate(delta > 0 ? addDays(currentDate, 1) : subDays(currentDate, 1));
    }
  };

  // Click handlers
  const handleSlotClick = (date: Date, hour?: number) => {
    const slotDate = hour !== undefined ? setMinutes(setHours(date, hour), 0) : date;
    setSelectedSlot(slotDate);
    setSelectedEvent(null);
    setIsModalOpen(true);
    onSlotClick?.(slotDate);
  };

  const handleEventClick = (event: CalendarEvent, e?: React.MouseEvent) => {
    e?.stopPropagation();
    // Hub Meetings / Trainings route to the full-screen EventDetailPage
    // inside the Activity Tracking tab. We stash the event id in
    // sessionStorage and switch tabs — EventTrackingPage reads the hint on
    // mount. When no onNavigate is available (e.g. embedded calendar) we
    // fall through to the default EventModal so the click still does
    // something useful.
    if (event.id != null && ACTIVITY_TRACKING_EVENT_TYPES.has(event.event_type) && onNavigate) {
      try {
        sessionStorage.setItem(ACTIVITY_EVENT_DEEPLINK_KEY, String(event.id));
      } catch {
        /* ignore — fall through to navigation anyway */
      }
      onNavigate('event-tracking');
      onEventClick?.(event);
      return;
    }
    setSelectedEvent(event);
    setSelectedSlot(null);
    setIsModalOpen(true);
    onEventClick?.(event);
  };

  const handleNewEvent = () => {
    setSelectedEvent(null);
    setSelectedSlot(zonedNow(tz));
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedEvent(null);
    setSelectedSlot(null);
  };

  const handleEventSaved = () => {
    handleModalClose();
    refetch();
  };

  // Get events for a specific day
  const getEventsForDay = (day: Date) => {
    return events.filter((event) => isSameDay(toZonedWallDate(event.start_time, tz), day));
  };

  // Get events for a specific hour
  const getEventsForHour = (day: Date, hour: number) => {
    return events.filter((event) => {
      const eventStart = toZonedWallDate(event.start_time, tz);
      return isSameDay(eventStart, day) && getHours(eventStart) === hour;
    });
  };

  // Render header
  const renderHeader = () => (
    <div className="flex flex-wrap items-center justify-between gap-2 border-b bg-white px-3 py-2.5 dark:bg-gray-800 sm:px-4 sm:py-3">
      <div className="flex min-w-0 items-center gap-2 sm:gap-4">
        <h2 className="truncate text-sm font-semibold text-gray-900 dark:text-white sm:text-lg">
          {view === 'day' && format(currentDate, 'EEE, MMM d, yyyy')}
          {view === 'week' && `${format(dateRange.start, 'MMM d')} - ${format(dateRange.end, 'MMM d')}`}
          {view === 'month' && format(currentDate, 'MMMM yyyy')}
        </h2>
        <span className="hidden whitespace-nowrap text-xs text-gray-500 dark:text-gray-400 sm:inline" data-testid="calendar-time-zone">
          {t('calendar.timesShownIn', 'Times in {{zone}}', { zone: zoneAbbreviation(Date.now(), tz) })}
        </span>
        {memberId && memberName && (
          <span className="hidden truncate text-sm text-gray-500 sm:inline">
            for {memberName}
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
        {/* View Switcher */}
        <div className="flex overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
          {(['day', 'week', 'month'] as CalendarView[]).map((v) => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={cn(
                'px-2 py-1 text-xs font-medium transition-colors sm:px-3 sm:py-1.5 sm:text-sm',
                view === v
                  ? 'bg-teal-600 text-white'
                  : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
              )}
            >
              {v.charAt(0).toUpperCase() + v.slice(1)}
            </button>
          ))}
        </div>

        {/* Navigation */}
        <div className="flex items-center gap-1">
          <button
            onClick={() => handleNavigate('prev')}
            className="rounded-lg p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 sm:p-2"
            title="Previous"
          >
            <ChevronLeft className="h-4 w-4 sm:h-5 sm:w-5" />
          </button>
          <button
            onClick={() => handleNavigate('today')}
            className="rounded-lg px-2 py-1 text-xs font-medium hover:bg-gray-100 dark:hover:bg-gray-700 sm:px-3 sm:py-1.5 sm:text-sm"
          >
            Today
          </button>
          <button
            onClick={() => handleNavigate('next')}
            className="rounded-lg p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 sm:p-2"
            title="Next"
          >
            <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5" />
          </button>
        </div>

        {/* Refresh */}
        <button
          onClick={() => refetch()}
          className="rounded-lg p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 sm:p-2"
          title="Refresh"
        >
          <RefreshCw className={cn('h-4 w-4 sm:h-5 sm:w-5', isLoading && 'animate-spin')} />
        </button>

        {/* New Event */}
        {showCreateButton && (
          <button
            onClick={handleNewEvent}
            className="flex items-center gap-1.5 rounded-lg bg-teal-600 px-2.5 py-1.5 text-xs font-medium text-white transition-colors hover:bg-teal-700 sm:gap-2 sm:px-4 sm:py-2 sm:text-sm"
          >
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">New Event</span>
            <span className="sm:hidden">New</span>
          </button>
        )}
      </div>
    </div>
  );

  // Render month view
  const renderMonthView = () => (
    <div className="grid grid-cols-7 flex-1 overflow-auto">
      {/* Day headers */}
      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
        <div
          key={day}
          className="p-2 text-center text-sm font-medium text-gray-500 dark:text-gray-400 border-b bg-gray-50 dark:bg-gray-800"
        >
          {day}
        </div>
      ))}

      {/* Day cells */}
      {days.map((day) => {
        const dayEvents = getEventsForDay(day);
        const isCurrentMonth = day.getMonth() === currentDate.getMonth();

        return (
          <div
            key={day.toISOString()}
            className={cn(
              'min-h-[120px] p-1 border-b border-r cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors',
              !isCurrentMonth && 'bg-gray-50 dark:bg-gray-900/50 text-gray-400'
            )}
            onClick={() => handleSlotClick(day)}
          >
            <div
              className={cn(
                'text-sm font-medium mb-1 w-7 h-7 flex items-center justify-center rounded-full',
                isTodayInZone(day) && 'bg-teal-600 text-white'
              )}
            >
              {format(day, 'd')}
            </div>

            <div className="space-y-1">
              {dayEvents.slice(0, 3).map((event) => (
                <EventChip key={`${event.id}-${event.occurrence_start ?? ''}`} event={event} onClick={handleEventClick} />
              ))}
              {dayEvents.length > 3 && (
                <div className="text-xs text-gray-500 dark:text-gray-400 pl-1">
                  +{dayEvents.length - 3} more
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );

  // Render week/day view
  const renderWeekDayView = () => {
    const hours = Array.from({ length: 18 }, (_, i) => i + 6); // 6 AM to 11 PM (midnight)

    return (
      <div className="flex flex-1 overflow-auto">
        {/* Time column */}
        <div className="w-16 flex-shrink-0 border-r bg-gray-50 dark:bg-gray-800">
          <div className="h-12 border-b" /> {/* Header spacer */}
          {hours.map((hour) => (
            <div
              key={hour}
              className="h-16 text-xs text-gray-500 dark:text-gray-400 text-right pr-2 pt-1 border-b"
            >
              {format(setHours(new Date(), hour), 'h a')}
            </div>
          ))}
        </div>

        {/* Day columns */}
        <div
          className={cn('flex-1 grid', view === 'week' ? 'grid-cols-7' : 'grid-cols-1')}
        >
          {days.map((day) => (
            <div key={day.toISOString()} className="relative border-r min-w-[120px]">
              {/* Day header */}
              <div
                className={cn(
                  'sticky top-0 z-10 bg-white dark:bg-gray-800 border-b p-2 text-center h-12',
                  isTodayInZone(day) && 'bg-teal-50 dark:bg-teal-900/20'
                )}
              >
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {format(day, 'EEE')}
                </div>
                <div
                  className={cn(
                    'text-lg font-semibold',
                    isTodayInZone(day) && 'text-teal-600 dark:text-teal-400'
                  )}
                >
                  {format(day, 'd')}
                </div>
              </div>

              {/* Hour slots */}
              {hours.map((hour) => {
                const slotEvents = getEventsForHour(day, hour);

                return (
                  <div
                    key={hour}
                    className="h-16 border-b border-gray-100 dark:border-gray-700 relative cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                    onClick={() => handleSlotClick(day, hour)}
                  >
                    {slotEvents.map((event) => (
                      <EventBlock key={`${event.id}-${event.occurrence_start ?? ''}`} event={event} onClick={handleEventClick} />
                    ))}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className={cn('flex flex-col h-full bg-white dark:bg-gray-900 rounded-lg shadow-sm border', className)}>
      {renderHeader()}
      {view === 'month' ? renderMonthView() : renderWeekDayView()}

      {/* Event Modal — appointments + member-attached events. Activity-
       *  tracking events route to the full-screen EventDetailPage inside the
       *  Activity Tracking tab (see handleEventClick). */}
      <EventModal
        open={isModalOpen}
        onClose={handleModalClose}
        event={selectedEvent}
        defaultDate={selectedSlot || undefined}
        memberId={memberId}
        memberName={memberName}
        onSaved={handleEventSaved}
      />
    </div>
  );
}

// Event chip for month view
function EventChip({
  event,
  onClick,
}: {
  event: CalendarEvent;
  onClick: (event: CalendarEvent, e?: React.MouseEvent) => void;
}) {
  const { fmt } = useFormattedDate();
  const zoneNote = useItemZoneNote()(event.start_time, event.timezone);
  const colorMap: Record<string, string> = {
    appointment: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
    meeting: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
    block: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
    out_of_office: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
    follow_up: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300',
    network_hub_meeting: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
    training_workshop: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  };

  return (
    <div
      className={cn(
        'text-xs px-1.5 py-0.5 rounded truncate cursor-pointer hover:opacity-80',
        colorMap[event.event_type] || 'bg-gray-100 text-gray-800'
      )}
      onClick={(e) => onClick(event, e)}
      title={zoneNote ? `${event.title} ${zoneNote}` : event.title}
    >
      {fmt.custom(event.start_time, 'h:mm a')} {event.title}
    </div>
  );
}

// Event block for week/day view
function EventBlock({
  event,
  onClick,
}: {
  event: CalendarEvent;
  onClick: (event: CalendarEvent, e?: React.MouseEvent) => void;
}) {
  const { fmt } = useFormattedDate();
  const colorMap: Record<string, string> = {
    appointment: 'bg-blue-500 border-blue-600',
    meeting: 'bg-purple-500 border-purple-600',
    block: 'bg-gray-400 border-gray-500',
    out_of_office: 'bg-orange-500 border-orange-600',
    follow_up: 'bg-teal-500 border-teal-600',
    network_hub_meeting: 'bg-amber-500 border-amber-600',
    training_workshop: 'bg-green-500 border-green-600',
  };

  const locationIcon = {
    in_person: <MapPin className="h-3 w-3" />,
    virtual: <Video className="h-3 w-3" />,
    phone: <Phone className="h-3 w-3" />,
    hybrid: <Users className="h-3 w-3" />,
  };

  return (
    <div
      className={cn(
        'absolute inset-x-1 top-1 rounded text-white text-xs p-1 cursor-pointer hover:opacity-90 border-l-2',
        colorMap[event.event_type] || 'bg-gray-500 border-gray-600'
      )}
      style={{ minHeight: '28px' }}
      onClick={(e) => onClick(event, e)}
    >
      <div className="font-medium truncate">{event.title}</div>
      <div className="flex items-center gap-1 text-white/80">
        {locationIcon[event.location_type]}
        <span>{fmt.custom(event.start_time, 'h:mm a')}</span>
        {/* TRUS-697: the event's own time when it was scheduled in another zone. */}
        <ItemZoneNote value={event.start_time} itemTimeZone={event.timezone} className="truncate" />
      </div>
    </div>
  );
}
